import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  Grid,
  CardContent,
  CardActions,
  CardHeader,
  Avatar,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  MenuItem,
  FormHelperText,
  IconButton,
  Pagination,
  Tab,
  InputAdornment,
} from '@mui/material';
import { leaderboardAPI, cachedLeaderboardAPI } from '../../services/api';
import { useTranslation } from 'react-i18next';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PeopleIcon from '@mui/icons-material/People';
import AddIcon from '@mui/icons-material/Add';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import CloseIcon from '@mui/icons-material/Close';
import FilterListIcon from '@mui/icons-material/FilterList';
import SearchIcon from '@mui/icons-material/Search';
import { format } from 'date-fns';
import { enGB, ru } from 'date-fns/locale';
import Button from '../UIKit/Button';
import Card from '../UIKit/Card';
import TextField from '../UIKit/TextField';
import Select from '../UIKit/Select';
import Tabs from '../UIKit/Tabs';
import { useLanguage } from '../../context/LanguageContext';
import CompetitionDetails from './CompetitionDetails';

const Competitions = ({ currentUser }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [competitions, setCompetitions] = useState([]);
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [newCompetition, setNewCompetition] = useState({
    title: '',
    description: '',
    startDate: '',
    endDate: '',
    type: 'seasonal',
    metric: 'experience',
    experienceMultiplier: 1.0,
    requirements: {
      minimumLevel: 1,
      roles: ['student', 'teacher', 'admin'],
    },
    rewards: {
      experience: {
        first: 500,
        second: 300,
        third: 200,
        participation: 50,
      },
    },
  });
  const [formErrors, setFormErrors] = useState({});
  const [joiningCompetitionId, setJoiningCompetitionId] = useState(null);
  const [createError, setCreateError] = useState(null);

  // Новые состояния для пагинации и фильтрации
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize, setPageSize] = useState(6);
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [metricFilter, setMetricFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [totalCompetitions, setTotalCompetitions] = useState(0);

  const [selectedCompetitionId, setSelectedCompetitionId] = useState(null);

  const { t } = useTranslation(['leaderboard', 'common']);
  const { currentLanguage } = useLanguage();

  // Функция для получения локали date-fns на основе текущего языка
  const getDateLocale = useCallback(() => {
    switch (currentLanguage) {
      case 'ru':
        return ru;
      case 'en':
      default:
        return enGB;
    }
  }, [currentLanguage]);

  // Обновленная функция загрузки данных с учетом пагинации и фильтров
  const fetchCompetitions = useCallback(async (
    pageNum = page,
    filters = {
      status: statusFilter,
      type: typeFilter,
      metric: metricFilter,
      search: searchQuery,
    }
  ) => {
    try {
      setLoading(true);
      setError(null);

      try {
        // Формируем параметры запроса
        const params = {
          page: pageNum,
          limit: pageSize,
          status: filters.status !== 'all' ? filters.status : undefined,
          type: filters.type !== 'all' ? filters.type : undefined,
          metric: filters.metric !== 'all' ? filters.metric : undefined,
          search: filters.search ? filters.search : undefined,
        };

        // Выполняем запрос
        const response = await leaderboardAPI.getCompetitions(params);

        // Проверяем структуру данных
        if (response.data && response.data.competitions) {
          setCompetitions(response.data.competitions);

          // Устанавливаем информацию о пагинации
          if (response.data.pagination) {
            setTotalPages(response.data.pagination.pages || 1);
            setTotalCompetitions(response.data.pagination.total || 0);
          }
        } else if (Array.isArray(response.data)) {
          setCompetitions(response.data);
          setTotalCompetitions(response.data.length);
          setTotalPages(Math.ceil(response.data.length / pageSize) || 1);
        } else {
          console.error('Неожиданный формат данных:', response.data);
          setCompetitions([]);
          setTotalCompetitions(0);
          setTotalPages(1);
        }
      } catch (apiError) {
        console.error('Ошибка API при загрузке соревнований:', apiError);

        try {
          const directResponse = await leaderboardAPI.getCompetitions({
            page: pageNum,
            limit: pageSize,
            status: filters.status !== 'all' ? filters.status : undefined,
            type: filters.type !== 'all' ? filters.type : undefined,
            metric: filters.metric !== 'all' ? filters.metric : undefined,
            search: filters.search ? filters.search : undefined,
          });

          if (directResponse.data && directResponse.data.competitions) {
            setCompetitions(directResponse.data.competitions);

            // Устанавливаем информацию о пагинации
            if (directResponse.data.pagination) {
              setTotalPages(directResponse.data.pagination.pages || 1);
              setTotalCompetitions(directResponse.data.pagination.total || 0);
            }
          } else if (Array.isArray(directResponse.data)) {
            setCompetitions(directResponse.data);
            setTotalCompetitions(directResponse.data.length);
            setTotalPages(
              Math.ceil(directResponse.data.length / pageSize) || 1
            );
          } else {
            throw new Error('Неверный формат данных даже при прямом запросе');
          }
        } catch (directError) {
          console.error('Ошибка прямого запроса:', directError);
          setCompetitions([]);
          setTotalCompetitions(0);
          setTotalPages(1);
        }
      }
    } catch (err) {
      console.error('Ошибка при загрузке соревнований:', err);
      setError(t('leaderboard:competitions.errorLoading'));
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter, typeFilter, metricFilter, searchQuery, pageSize, t]);

  // Вызываем загрузку данных при изменении фильтров или страницы
  useEffect(() => {
    fetchCompetitions(page, {
      status: statusFilter,
      type: typeFilter,
      metric: metricFilter,
      search: searchQuery,
    });
  }, [page, statusFilter, typeFilter, metricFilter, currentUser._id, fetchCompetitions, searchQuery]);

  // Обработчик изменения страницы
  const handlePageChange = useCallback((event, value) => {
    setPage(value);
  }, []);

  // Обработчик изменения фильтра статуса
  const handleStatusFilterChange = useCallback((event, newValue) => {
    setStatusFilter(newValue);
    setPage(1); // Сбрасываем на первую страницу при изменении фильтра
  }, []);

  // Обработчик поиска
  const handleSearch = useCallback(() => {
    setPage(1); // Сбрасываем на первую страницу при поиске
    fetchCompetitions(1, {
      status: statusFilter,
      type: typeFilter,
      metric: metricFilter,
      search: searchQuery,
    });
  }, [fetchCompetitions, statusFilter, typeFilter, metricFilter, searchQuery]);

  // Обработчик сброса фильтров
  const handleResetFilters = useCallback(() => {
    setStatusFilter('all');
    setTypeFilter('all');
    setMetricFilter('all');
    setSearchQuery('');
    setPage(1);
    fetchCompetitions(1, {
      status: 'all',
      type: 'all',
      metric: 'all',
      search: '',
    });
  }, [fetchCompetitions]);

  const handleCreateDialogOpen = useCallback(() => {
    setOpenCreateDialog(true);
  }, []);

  const handleCreateDialogClose = useCallback(() => {
    setOpenCreateDialog(false);
    setFormErrors({});
  }, []);

  const validateForm = useCallback(() => {
    const errors = {};

    // Базовая валидация полей
    if (!newCompetition.title.trim()) {
      errors.title = t('leaderboard:competitions.form.validation.titleRequired');
    }

    if (!newCompetition.description.trim()) {
      errors.description = t('leaderboard:competitions.form.validation.descriptionRequired');
    }

    if (!newCompetition.startDate) {
      errors.startDate = t('leaderboard:competitions.form.validation.startDateRequired');
    } else {
      const startDate = new Date(newCompetition.startDate);
      if (isNaN(startDate.getTime())) {
        errors.startDate = t('leaderboard:competitions.form.validation.invalidDate');
      } else if (startDate <= new Date()) {
        errors.startDate = t('leaderboard:competitions.form.validation.startDateFuture');
      }
    }

    if (!newCompetition.endDate) {
      errors.endDate = t('leaderboard:competitions.form.validation.endDateRequired');
    } else {
      const startDate = new Date(newCompetition.startDate);
      const endDate = new Date(newCompetition.endDate);

      if (isNaN(endDate.getTime())) {
        errors.endDate = t('leaderboard:competitions.form.validation.invalidDate');
      } else if (endDate <= startDate) {
        errors.endDate = t('leaderboard:competitions.form.validation.endDateAfterStart');
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  }, [newCompetition, t]);

  const handleCreateCompetition = useCallback(async () => {
    if (!validateForm()) return;

    try {
      setLoading(true);
      // Сбрасываем ошибку перед запросом
      setCreateError(null);

      // Подготовка данных для отправки
      const competitionData = {
        ...newCompetition,
        // Преобразуем даты в ISO формат
        startDate: new Date(newCompetition.startDate).toISOString(),
        endDate: new Date(newCompetition.endDate).toISOString(),
      };

      await leaderboardAPI.createCompetition(competitionData);

      // Перезагрузка списка соревнований с текущими фильтрами и страницей
      fetchCompetitions(page, {
        status: statusFilter,
        type: typeFilter,
        metric: metricFilter,
        search: searchQuery,
      });

      // Закрытие диалога и сброс формы только при успешном создании
      setOpenCreateDialog(false);
      setNewCompetition({
        title: '',
        description: '',
        startDate: '',
        endDate: '',
        type: 'seasonal',
        metric: 'experience',
        experienceMultiplier: 1.0,
        requirements: {
          minimumLevel: 1,
          roles: ['student', 'teacher', 'admin'],
        },
        rewards: {
          experience: {
            first: 500,
            second: 300,
            third: 200,
            participation: 50,
          },
        },
      });
    } catch (err) {
      console.error('Ошибка при создании соревнования:', err);

      // Пытаемся извлечь подробное сообщение об ошибке
      if (err.response && err.response.data) {
        if (err.response.data.message) {
          setCreateError(err.response.data.message);
        } else if (err.response.data.error) {
          setCreateError(err.response.data.error);
        } else if (typeof err.response.data === 'string') {
          setCreateError(err.response.data);
        } else {
          setCreateError(`Ошибка: ${err.message || 'Неизвестная ошибка'}`);
        }
      } else {
        setCreateError(t('leaderboard:competitions.errorCreating'));
      }
    } finally {
      setLoading(false);
    }
  }, [validateForm, newCompetition, fetchCompetitions, page, statusFilter, typeFilter, metricFilter, searchQuery, t]);

  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;

    // Обработка вложенных свойств объекта (например, rewards.experience.first)
    if (name.includes('.')) {
      const parts = name.split('.');
      if (parts.length === 2) {
        // Обработка свойств первого уровня вложенности (например, requirements.minimumLevel)
        setNewCompetition((prev) => ({
          ...prev,
          [parts[0]]: {
            ...prev[parts[0]],
            [parts[1]]: ['minimumLevel'].includes(parts[1])
              ? parseInt(value, 10) || 1
              : value,
          },
        }));
      } else if (parts.length === 3) {
        // Обработка свойств второго уровня вложенности (например, rewards.experience.first)
        setNewCompetition((prev) => ({
          ...prev,
          [parts[0]]: {
            ...prev[parts[0]],
            [parts[1]]: {
              ...prev[parts[0]][parts[1]],
              [parts[2]]: [
                'first',
                'second',
                'third',
                'participation',
              ].includes(parts[2])
                ? parseInt(value, 10) || 0
                : value,
            },
          },
        }));
      }
    } else {
      // Обычное обновление для свойств верхнего уровня
      setNewCompetition((prev) => ({
        ...prev,
        [name]: ['experienceMultiplier'].includes(name)
          ? parseFloat(value) || 1.0
          : value,
      }));
    }

    // Очистка ошибки при вводе
    if (formErrors[name]) {
      setFormErrors((prev) => ({
        ...prev,
        [name]: null,
      }));
    }
  }, [formErrors]);

  const handleJoinCompetition = useCallback(async (competitionId) => {
    try {
      setJoiningCompetitionId(competitionId);

      await leaderboardAPI.joinCompetition(competitionId);

      // Обновляем список соревнований после присоединения
      const response = await cachedLeaderboardAPI.getCompetitions();
      setCompetitions(response.data.competitions);
    } catch (err) {
      console.error('Ошибка при присоединении к соревнованию:', err);
      setError(t('leaderboard:competitions.errorJoining'));
    } finally {
      setJoiningCompetitionId(null);
    }
  }, [t]);

  // Функция для отображения статуса соревнования
  const getCompetitionStatus = useCallback((competition) => {
    const now = new Date();
    const startDate = new Date(competition.startDate);
    const endDate = new Date(competition.endDate);

    if (now < startDate) {
      return {
        label: t('leaderboard:competitions.status.planned'),
        color: 'warning',
      };
    } else if (now >= startDate && now <= endDate) {
      return {
        label: t('leaderboard:competitions.status.active'),
        color: 'success',
      };
    } else {
      return {
        label: t('leaderboard:competitions.status.completed'),
        color: 'default',
      };
    }
  }, [t]);

  // Функция для проверки, присоединился ли текущий пользователь к соревнованию
  const isUserJoined = useCallback((competition) => {
    return competition.participants?.some((p) => p.userId === currentUser?._id);
  }, [currentUser?._id]);

  // Функция для отображения типа соревнования
  const getCompetitionType = useCallback((type) => {
    switch (type) {
      case 'seasonal':
        return t('leaderboard:competitions.types.seasonal');
      case 'challenge':
        return t('leaderboard:competitions.types.challenge');
      case 'event':
        return t('leaderboard:competitions.types.event');
      case 'tournament':
        return t('leaderboard:competitions.types.tournament');
      default:
        return t('common:unknown');
    }
  }, [t]);

  // Функция для отображения метрики соревнования
  const getCompetitionMetric = useCallback((metric) => {
    switch (metric) {
      case 'experience':
        return t('leaderboard:competitions.metrics.experience');
      case 'courses':
        return t('leaderboard:competitions.metrics.courses');
      case 'tests':
        return t('leaderboard:competitions.metrics.tests');
      case 'skill':
        return t('leaderboard:competitions.metrics.skill');
      case 'streak':
        return t('leaderboard:competitions.metrics.streak');
      default:
        return t('common:unknown');
    }
  }, [t]);

  // Функция для проверки наличия всех необходимых полей в объекте соревнования
  const isValidCompetition = useCallback((competition) => {
    // Проверяем наличие обязательных полей
    const requiredFields = [
      '_id',
      'title',
      'description',
      'startDate',
      'endDate',
      'status',
    ];
    for (const field of requiredFields) {
      if (!competition[field]) {
        console.warn(
          `Соревнование не содержит обязательное поле ${field}:`,
          competition
        );
        return false;
      }
    }
    return true;
  }, []);

  // Получение стандартной цели по типу метрики
  const getDefaultGoalByMetric = useCallback((metric) => {
    switch (metric) {
      case 'experience':
        return 1000;
      case 'courses':
        return 5;
      case 'tests':
        return 10;
      case 'skill':
        return 100;
      case 'streak':
        return 30;
      default:
        return 100;
    }
  }, []);

  // Функция для расчета прогресса соревнования
  const calculateProgress = useCallback((competition, user) => {
    if (!competition || !user || !competition.participants) {
      return 0;
    }

    // Находим участника
    const participant = competition.participants.find((p) => {
      if (typeof p.userId === 'object' && p.userId._id) {
        return p.userId._id.toString() === user._id.toString();
      }
      return p.userId && p.userId.toString() === user._id.toString();
    });

    if (!participant) {
      return 0;
    }

    // Если есть score, используем его, иначе используем progress
    const score =
      participant.score !== undefined
        ? participant.score
        : participant.progress || 0;

    // Если есть цель, используем ее, иначе используем предполагаемое значение по метрике
    const goal = competition.goal || getDefaultGoalByMetric(competition.metric);

    return Math.min(100, (score / goal) * 100);
  }, [getDefaultGoalByMetric]);

  // Мемоизация фильтрованных соревнований
  const filteredCompetitions = useMemo(() => {
    return competitions.filter(isValidCompetition);
  }, [competitions, isValidCompetition]);

  if (loading && competitions.length === 0) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="300px"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error && competitions.length === 0) {
    return (
      <Box my={3}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h5" display="flex" alignItems="center" gap={1}>
          <TrendingUpIcon />
          {t('leaderboard:competitions.title')}
        </Typography>

        {currentUser && currentUser.role !== 'student' && (
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleCreateDialogOpen}
          >
            {t('leaderboard:competitions.create')}
          </Button>
        )}
      </Box>

      {/* Фильтры соревнований */}
      <Paper elevation={0} sx={{ borderRadius: '16px', p: 2, mb: 3 }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography variant="subtitle1" fontWeight="bold">
            {t('leaderboard:competitions.filters')}
          </Typography>
          <IconButton
            size="small"
            onClick={() => setShowFilters(!showFilters)}
            color={showFilters ? 'primary' : 'default'}
          >
            <FilterListIcon />
          </IconButton>
        </Box>

        {/* Основной фильтр по статусу - всегда видимый */}
        <Box
          sx={{
            bgcolor: 'background.default',
            borderRadius: { xs: '12px', sm: '16px' },
            p: '4px',
            width: '100%',
            overflow: 'hidden',
            mb: 3,
          }}
        >
          <Tabs value={statusFilter} onChange={handleStatusFilterChange}>
            <Tab label={t('leaderboard:competitions.status.all')} value="all" />
            <Tab
              label={t('leaderboard:competitions.status.active')}
              value="active"
            />
            <Tab
              label={t('leaderboard:competitions.status.upcoming')}
              value="upcoming"
            />
            <Tab
              label={t('leaderboard:competitions.status.completed')}
              value="completed"
            />
          </Tabs>
        </Box>

        {/* Дополнительные фильтры - скрываемые */}
        {showFilters && (
          <Box sx={{ mb: 2 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth size="small">
                  <Select
                    labelId="type-filter-label"
                    value={typeFilter}
                    label={t('leaderboard:competitions.types.all')}
                    onChange={(e) => {
                      setTypeFilter(e.target.value);
                      setPage(1);
                    }}
                  >
                    <MenuItem value="all">
                      {t('leaderboard:competitions.types.all')}
                    </MenuItem>
                    <MenuItem value="seasonal">
                      {t('leaderboard:competitions.types.seasonal')}
                    </MenuItem>
                    <MenuItem value="challenge">
                      {t('leaderboard:competitions.types.challenge')}
                    </MenuItem>
                    <MenuItem value="event">
                      {t('leaderboard:competitions.types.event')}
                    </MenuItem>
                    <MenuItem value="tournament">
                      {t('leaderboard:competitions.types.tournament')}
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={4}>
                <FormControl fullWidth size="small">
                  <Select
                    labelId="metric-filter-label"
                    value={metricFilter}
                    label={t('leaderboard:competitions.metrics.all')}
                    onChange={(e) => {
                      setMetricFilter(e.target.value);
                      setPage(1);
                    }}
                  >
                    <MenuItem value="all">
                      {t('leaderboard:competitions.metrics.all')}
                    </MenuItem>
                    <MenuItem value="experience">
                      {t('leaderboard:competitions.metrics.experience')}
                    </MenuItem>
                    <MenuItem value="courses">
                      {t('leaderboard:competitions.metrics.courses')}
                    </MenuItem>
                    <MenuItem value="tests">
                      {t('leaderboard:competitions.metrics.tests')}
                    </MenuItem>
                    <MenuItem value="skill">
                      {t('leaderboard:competitions.metrics.skill')}
                    </MenuItem>
                    <MenuItem value="streak">
                      {t('leaderboard:competitions.metrics.streak')}
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  size="small"
                  label={t('leaderboard:competitions.searchPlaceholder')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={handleSearch}>
                          <SearchIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>

            <Box mt={2} display="flex" justifyContent="flex-end">
              <Button
                variant="outlined"
                color="error"
                size="small"
                onClick={handleResetFilters}
                startIcon={<CloseIcon />}
              >
                {t('leaderboard:competitions.resetFilters')}
              </Button>
            </Box>
          </Box>
        )}
      </Paper>

      {/* Статистика результатов поиска */}
      <Box
        mb={2}
        display="flex"
        justifyContent="space-between"
        alignItems="center"
      >
        <Typography variant="body2" color="text.secondary">
          {t('leaderboard:competitions.foundCompetitions')}: {totalCompetitions}
          {(statusFilter !== 'all' ||
            typeFilter !== 'all' ||
            metricFilter !== 'all' ||
            searchQuery) &&
            ` (${t('leaderboard:competitions.withFilters')})`}
        </Typography>

        <FormControl size="small" sx={{ minWidth: 120 }}>
          <Select
            value={pageSize}
            onChange={(e) => {
              setPageSize(e.target.value);
              setPage(1);
              fetchCompetitions(1, {
                status: statusFilter,
                type: typeFilter,
                metric: metricFilter,
                search: searchQuery,
              });
            }}
            displayEmpty
            variant="outlined"
            size="small"
          >
            <MenuItem value={6}>
              6 {t('leaderboard:competitions.perPage')}
            </MenuItem>
            <MenuItem value={12}>
              12 {t('leaderboard:competitions.perPage')}
            </MenuItem>
            <MenuItem value={24}>
              24 {t('leaderboard:competitions.perPage')}
            </MenuItem>
          </Select>
        </FormControl>
      </Box>

      {competitions.length === 0 ? (
        <Paper
          elevation={0}
          sx={{ borderRadius: '16px', p: 2, textAlign: 'center' }}
        >
          <Typography variant="h6" color="text.secondary" mb={2}>
            {t('leaderboard:competitions.noCompetitions')}
          </Typography>
          {currentUser && currentUser.role !== 'student' && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleCreateDialogOpen}
            >
              {t('leaderboard:competitions.createFirst')}
            </Button>
          )}
        </Paper>
      ) : (
        <>
          <Grid container spacing={3}>
            {filteredCompetitions.map((competition) => {
              const status = getCompetitionStatus(competition);
              const joined = isUserJoined(competition);
              const now = new Date();
              const startDate = new Date(competition.startDate);
              const canJoin = currentUser && !joined && now <= startDate;
              const isActive =
                now >= startDate && now <= new Date(competition.endDate);

              // Рассчитываем прогресс для активных соревнований
              const progress =
                currentUser && joined && isActive
                  ? calculateProgress(competition, currentUser)
                  : 0;

              return (
                <Grid
                  item
                  key={competition._id}
                  xs={12}
                  md={6}
                  lg={pageSize === 24 ? 3 : pageSize === 12 ? 4 : 6}
                >
                  <Card
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    <CardHeader
                      avatar={
                        <Avatar sx={{ bgcolor: 'primary.main' }}>
                          <EmojiEventsIcon />
                        </Avatar>
                      }
                      title={competition.title}
                      subheader={
                        <Box
                          display="flex"
                          alignItems="center"
                          gap={1}
                          mt={0.5}
                          flexWrap="wrap"
                        >
                          <Chip
                            label={status.label}
                            color={status.color}
                            size="small"
                          />
                          <Chip
                            label={getCompetitionType(competition.type)}
                            variant="outlined"
                            size="small"
                          />
                        </Box>
                      }
                    />

                    <CardContent sx={{ flexGrow: 1 }}>
                      <Typography variant="body2" color="text.secondary" mb={2}>
                        {competition.description}
                      </Typography>

                      <Box display="flex" alignItems="center" mb={1}>
                        <CalendarTodayIcon
                          fontSize="small"
                          sx={{ mr: 1, color: 'text.secondary' }}
                        />
                        <Typography variant="body2" color="text.secondary">
                          {format(
                            new Date(competition.startDate),
                            'dd MMMM yyyy HH:mm',
                            { locale: getDateLocale() }
                          )}{' '}
                          -
                          {format(
                            new Date(competition.endDate),
                            'dd MMMM yyyy HH:mm',
                            { locale: getDateLocale() }
                          )}
                        </Typography>
                      </Box>

                      <Box display="flex" alignItems="center" mb={2}>
                        <PeopleIcon
                          fontSize="small"
                          sx={{ mr: 1, color: 'text.secondary' }}
                        />
                        <Typography variant="body2" color="text.secondary">
                          {t('leaderboard:competitions.participants')}: {competition.participants?.length || competition.participantsCount || 0}
                        </Typography>
                      </Box>

                      {competition.metric && (
                        <Box mb={1}>
                          <Typography variant="body2" fontWeight="bold">
                            {t('leaderboard:competitions.metricLabel')}: {getCompetitionMetric(competition.metric)}
                            {competition.experienceMultiplier && competition.experienceMultiplier !== 1 &&
                              ` (${t('leaderboard:competitions.multiplier', { value: competition.experienceMultiplier })})`}
                          </Typography>
                        </Box>
                      )}

                      {joined && isActive && (
                        <Box mt={2}>
                          <Typography
                            variant="body2"
                            display="flex"
                            justifyContent="space-between"
                          >
                            <span>
                              {t('leaderboard:competitions.yourProgress')}:
                            </span>
                            <span>{progress.toFixed(0)}%</span>
                          </Typography>
                          <LinearProgress
                            variant="determinate"
                            value={progress}
                            sx={{ mt: 1, height: 8, borderRadius: 1 }}
                          />
                        </Box>
                      )}

                      {competition.rewards && (
                        <Box mt={2}>
                          <Typography
                            variant="body2"
                            fontWeight="bold"
                            color="primary"
                          >
                            {t('leaderboard:competitions.rewardLabel')}: {t('leaderboard:competitions.rewardFirst', { value: competition.rewards.experience?.first })}
                            {competition.rewards.experience?.second &&
                              `, ${t('leaderboard:competitions.rewardSecond', { value: competition.rewards.experience.second })}`}
                            {competition.rewards.experience?.third &&
                              `, ${t('leaderboard:competitions.rewardThird', { value: competition.rewards.experience.third })}`}
                            {competition.rewards.experience?.participation &&
                              `, ${t('leaderboard:competitions.rewardParticipation', { value: competition.rewards.experience.participation })}`}
                          </Typography>
                        </Box>
                      )}
                    </CardContent>

                    <CardActions>
                      {canJoin ? (
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={() => handleJoinCompetition(competition._id)}
                          disabled={joiningCompetitionId === competition._id}
                          startIcon={
                            joiningCompetitionId === competition._id ? (
                              <CircularProgress size={16} />
                            ) : null
                          }
                        >
                          {joiningCompetitionId === competition._id
                            ? t('leaderboard:competitions.joining')
                            : t('leaderboard:competitions.join')}
                        </Button>
                      ) : joined ? (
                        <Chip
                          color="success"
                          label={t('leaderboard:competitions.participating')}
                        />
                      ) : (
                        <Button variant="outlined" color="primary" disabled>
                          {now > startDate
                            ? t('leaderboard:competitions.alreadyStarted')
                            : t('leaderboard:competitions.startsSoon')}
                        </Button>
                      )}

                      <Button
                        variant="outlined"
                        color="secondary"
                        sx={{ ml: 'auto' }}
                        onClick={() => setSelectedCompetitionId(competition._id)}
                      >
                        {t('leaderboard:competitions.details')}
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>
              );
            })}
          </Grid>

          {/* Пагинация */}
          {totalPages > 1 && (
            <Box mt={4} display="flex" justifyContent="center">
              <Pagination
                count={totalPages}
                page={page}
                onChange={handlePageChange}
                color="primary"
                siblingCount={1}
                boundaryCount={1}
              />
            </Box>
          )}
        </>
      )}

      {/* Диалоговое окно создания соревнования */}
      <Dialog
        open={openCreateDialog}
        onClose={handleCreateDialogClose}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: '16px',
          },
        }}
      >
        <DialogTitle>
          {t('leaderboard:competitions.form.title')}
          <IconButton
            onClick={handleCreateDialogClose}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          {createError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {createError}
            </Alert>
          )}

          <Box component="form" noValidate>
            <TextField
              margin="normal"
              required
              fullWidth
              id="title"
              label={t('leaderboard:competitions.form.nameLabel')}
              name="title"
              value={newCompetition.title}
              onChange={handleInputChange}
              error={!!formErrors.title}
              helperText={formErrors.title}
              autoFocus
            />

            <TextField
              margin="normal"
              required
              fullWidth
              multiline
              rows={3}
              id="description"
              label={t('leaderboard:competitions.form.descriptionLabel')}
              name="description"
              value={newCompetition.description}
              onChange={handleInputChange}
              error={!!formErrors.description}
              helperText={formErrors.description}
            />

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="startDate"
                  label={t('leaderboard:competitions.form.startDate')}
                  name="startDate"
                  type="datetime-local"
                  value={newCompetition.startDate}
                  onChange={handleInputChange}
                  error={!!formErrors.startDate}
                  helperText={formErrors.startDate}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ lang: currentLanguage }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="endDate"
                  label={t('leaderboard:competitions.form.endDate')}
                  name="endDate"
                  type="datetime-local"
                  value={newCompetition.endDate}
                  onChange={handleInputChange}
                  error={!!formErrors.endDate}
                  helperText={formErrors.endDate}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ lang: currentLanguage }}
                />
              </Grid>
            </Grid>

            <FormControl fullWidth margin="normal" required>
              <Select
                labelId="type-label"
                id="type"
                name="type"
                value={newCompetition.type}
                label={t('leaderboard:competitions.form.typeLabel')}
                onChange={handleInputChange}
              >
                <MenuItem value="seasonal">
                  {t('leaderboard:competitions.types.seasonal')}
                </MenuItem>
                <MenuItem value="challenge">
                  {t('leaderboard:competitions.types.challenge')}
                </MenuItem>
                <MenuItem value="event">
                  {t('leaderboard:competitions.types.event')}
                </MenuItem>
                <MenuItem value="tournament">
                  {t('leaderboard:competitions.types.tournament')}
                </MenuItem>
              </Select>
              <FormHelperText sx={{ ml: 0 }}>
                {t('leaderboard:competitions.form.typeHelper')}
              </FormHelperText>
            </FormControl>

            <FormControl fullWidth margin="normal" required>
              <Select
                labelId="metric-label"
                id="metric"
                name="metric"
                value={newCompetition.metric}
                label={t('leaderboard:competitions.form.metricLabel')}
                onChange={handleInputChange}
              >
                <MenuItem value="experience">
                  {t('leaderboard:competitions.metrics.experience')}
                </MenuItem>
                <MenuItem value="courses">
                  {t('leaderboard:competitions.metrics.courses')}
                </MenuItem>
                <MenuItem value="tests">
                  {t('leaderboard:competitions.metrics.tests')}
                </MenuItem>
                <MenuItem value="skill">
                  {t('leaderboard:competitions.metrics.skill')}
                </MenuItem>
                <MenuItem value="streak">
                  {t('leaderboard:competitions.metrics.streak')}
                </MenuItem>
              </Select>
              <FormHelperText sx={{ ml: 0 }}>
                {t('leaderboard:competitions.form.metricHelper')}
              </FormHelperText>
            </FormControl>

            <TextField
              margin="normal"
              fullWidth
              id="experienceMultiplier"
              label={t('leaderboard:competitions.form.multiplierLabel')}
              name="experienceMultiplier"
              type="number"
              inputProps={{ step: 0.1, min: 0.1 }}
              value={newCompetition.experienceMultiplier}
              onChange={handleInputChange}
              helperText={t('leaderboard:competitions.form.multiplierHelper')}
            />

            <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
              {t('leaderboard:competitions.form.rewardsTitle')}
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  margin="normal"
                  fullWidth
                  id="rewards.experience.first"
                  label={t('leaderboard:competitions.form.rewardFirst')}
                  name="rewards.experience.first"
                  type="number"
                  value={newCompetition.rewards.experience.first}
                  onChange={handleInputChange}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  margin="normal"
                  fullWidth
                  id="rewards.experience.second"
                  label={t('leaderboard:competitions.form.rewardSecond')}
                  name="rewards.experience.second"
                  type="number"
                  value={newCompetition.rewards.experience.second}
                  onChange={handleInputChange}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  margin="normal"
                  fullWidth
                  id="rewards.experience.third"
                  label={t('leaderboard:competitions.form.rewardThird')}
                  name="rewards.experience.third"
                  type="number"
                  value={newCompetition.rewards.experience.third}
                  onChange={handleInputChange}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  margin="normal"
                  fullWidth
                  id="rewards.experience.participation"
                  label={t('leaderboard:competitions.form.rewardParticipation')}
                  name="rewards.experience.participation"
                  type="number"
                  value={newCompetition.rewards.experience.participation}
                  onChange={handleInputChange}
                />
              </Grid>
            </Grid>

            <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
              {t('leaderboard:competitions.form.requirementsTitle')}
            </Typography>

            <TextField
              margin="normal"
              fullWidth
              id="requirements.minimumLevel"
              label={t('leaderboard:competitions.form.minLevelLabel')}
              name="requirements.minimumLevel"
              type="number"
              inputProps={{ min: 1 }}
              value={newCompetition.requirements.minimumLevel}
              onChange={handleInputChange}
              helperText={t('leaderboard:competitions.form.minLevelHelper')}
            />
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCreateDialogClose} variant="outlined">
            {t('common:actions.cancel')}
          </Button>
          <Button
            onClick={handleCreateCompetition}
            variant="contained"
            color="primary"
            disabled={loading}
          >
            {loading
              ? t('leaderboard:competitions.form.creating')
              : t('leaderboard:competitions.form.create')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(selectedCompetitionId)}
        onClose={() => setSelectedCompetitionId(null)}
        fullWidth
        maxWidth="md"
        PaperProps={{ sx: { borderRadius: '16px' } }}
      >
        <DialogTitle>
          {t('leaderboard:competitions.details')}
          <IconButton
            onClick={() => setSelectedCompetitionId(null)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <CompetitionDetails competitionId={selectedCompetitionId} />
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default Competitions;
